"use client";

import { useEffect, useRef, useState } from "react";
import { Music, Pause, Play, Radio } from "lucide-react";
import { fetchPublicNowPlaying } from "@/lib/api";

const SECURENET_PLAYER_URL = "https://streamdb7web.securenetsystems.net/cirruspremier/KMEE";
const SECURENET_SIDEKICK_URL =
  "https://streamdb7web.securenetsystems.net/embed/sidekickplayer/embed.js?stationCallSign=KMEE&playerlocation=streamdb7web&embedPosition=page&embedTheme=light&embedSize=small&embedOrientation=horizontal&autoPlay=true&embedWidthType=fixed&embedWidth=420";
const ICECAST_STREAM_URL = "https://ice41.securenetsystems.net/KMEE";
const MOBILE_DEVICE_QUERY =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i;

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    mobile?: boolean;
  };
};

function isDesktopPlayerDevice() {
  if (typeof window === "undefined") {
    return false;
  }

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const userAgentDataMobile = (navigator as NavigatorWithUserAgentData).userAgentData?.mobile === true;
  const isIpadDesktopMode = platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const isPhoneOrTablet = userAgentDataMobile || MOBILE_DEVICE_QUERY.test(userAgent) || isIpadDesktopMode;

  return !isPhoneOrTablet;
}

function querySidekickButton(kind: "play" | "pause") {
  const root = document.getElementById("sunny-secure-player-footer");
  return root?.querySelector(
    kind === "pause" ? ".ssiFloatingPlayer_pauseButton" : ".ssiFloatingPlayer_playButton",
  ) as HTMLElement | null;
}

function isSidekickPlaying() {
  const pause = querySidekickButton("pause");
  if (!pause) return false;
  const style = pause.getAttribute("style") || "";
  if (/display:\s*none/i.test(style)) return false;
  return getComputedStyle(pause).display !== "none";
}

function SecureNetSidekickPlayer() {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadTimer = window.setTimeout(() => {
      if (!hostRef.current || hostRef.current.querySelector("script")) {
        return;
      }

      const script = document.createElement("script");
      script.src = `${SECURENET_SIDEKICK_URL}&b=${Math.random()}`;
      script.async = true;
      hostRef.current.appendChild(script);
    }, 0);

    return () => window.clearTimeout(loadTimer);
  }, []);

  return <div ref={hostRef} className="h-[76px] w-full max-w-[420px] overflow-hidden bg-white" />;
}

function StreamToggleButton() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const audio = new Audio();
    audio.preload = "none";
    audioRef.current = audio;
    const syncFromEmbed = () => {
      if (querySidekickButton("play") || querySidekickButton("pause")) {
        setPlaying(isSidekickPlaying());
      }
    };
    const id = window.setInterval(syncFromEmbed, 400);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    audio.addEventListener("playing", onPlay);
    audio.addEventListener("pause", onPause);
    return () => {
      window.clearInterval(id);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
      audio.removeEventListener("playing", onPlay);
      audio.removeEventListener("pause", onPause);
    };
  }, []);

  return (
    <button
      type="button"
      aria-label={playing ? "Pause live stream" : "Play live stream"}
      aria-pressed={playing}
      onClick={() => {
        const pauseBtn = querySidekickButton("pause");
        const playBtn = querySidekickButton("play");
        if (pauseBtn || playBtn) {
          (isSidekickPlaying() ? pauseBtn : playBtn)?.click();
          window.setTimeout(() => setPlaying(isSidekickPlaying()), 50);
          return;
        }

        const audio = audioRef.current;
        if (!audio) return;
        if (!audio.src) audio.src = ICECAST_STREAM_URL;
        if (audio.paused) {
          void audio.play();
        } else {
          audio.pause();
        }
      }}
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      {playing ? <Pause className="h-5 w-5" fill="currentColor" /> : <Play className="ml-0.5 h-5 w-5" fill="currentColor" />}
    </button>
  );
}

export function UniversalPlayer() {
  const [isDesktop, setIsDesktop] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<{
    title?: string;
    artist?: string;
    album?: string | null;
    cover?: string | null;
  } | null>(null);

  useEffect(() => {
    setIsDesktop(isDesktopPlayerDevice());
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchPublicNowPlaying();
        if (!cancelled) {
          setNowPlaying({
            title: data.title,
            artist: data.artist,
            album: data.album,
            cover: data.cover,
          });
        }
      } catch {
        if (!cancelled) {
          setNowPlaying({ title: "Sunny 106.5", artist: "Streaming Live" });
        }
      }
    };
    load();
    const id = window.setInterval(load, 15_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return (
    <div
      id="sunny-secure-player-footer"
      className="fixed inset-x-0 bottom-0 z-[70] border-t border-slate-200 bg-white shadow-[0_-10px_35px_rgba(15,23,42,0.16)]"
    >
      {isDesktop ? (
        <div className="mx-auto flex max-w-7xl items-stretch gap-3 px-4 py-2">
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-900 shadow-sm">
              {nowPlaying?.cover ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={nowPlaying.cover}
                  alt={`${nowPlaying.artist} - ${nowPlaying.title}`}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Radio className="h-6 w-6 text-primary" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.14)]" />
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Live on Sunny 106.5</p>
              </div>
              <p className="truncate font-display text-base font-bold leading-tight text-slate-950">
                {nowPlaying?.title || "Sunny 106.5"}
              </p>
              <p className="truncate text-xs font-medium text-slate-600">
                {nowPlaying?.artist || "Streaming Live"}
              </p>
              {nowPlaying?.album && <p className="truncate text-[11px] text-slate-500">{nowPlaying.album}</p>}
            </div>

            <StreamToggleButton />
          </div>

          <div className="h-[84px] w-full max-w-[min(432px,90vw)] shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm">
            <SecureNetSidekickPlayer />
          </div>
        </div>
      ) : (
        <div className="mx-auto flex max-w-6xl items-stretch gap-3 px-3 py-2">
          <div className="flex w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-100">
            <Music className="h-5 w-5 text-primary" />
          </div>

          <iframe
            src={SECURENET_PLAYER_URL}
            title="Sunny 106.5 live player"
            className="h-[132px] min-w-0 flex-1 rounded-md border-0 bg-white"
            allow="autoplay; encrypted-media"
            scrolling="no"
          />
        </div>
      )}
    </div>
  );
}
